import { css, useTheme } from '@emotion/react';
import { ListChecks, Mic, Music, Pencil, Sparkles } from 'lucide-react';

import { spacing } from '@/theme/tokens';

import type { WorkflowStep } from './workflow-utils';

interface WorkflowStepBarProps {
  currentStep: WorkflowStep;
  viewingStep: WorkflowStep;
  onStepClick: (step: WorkflowStep) => void;
}

const STEPS: { key: WorkflowStep; label: string; icon: typeof Pencil }[] = [
  { key: 'write', label: 'Write', icon: Pencil },
  { key: 'analyze', label: 'Analyze', icon: Sparkles },
  { key: 'review', label: 'Review', icon: ListChecks },
  { key: 'rehearse', label: 'Rehearse', icon: Mic },
  { key: 'generate', label: 'Generate', icon: Music },
];

function getStepIndex(step: WorkflowStep): number {
  // 'complete' shares the generate position
  if (step === 'complete') return 4;
  const idx = STEPS.findIndex((s) => s.key === step);
  return idx === -1 ? 0 : idx;
}

export function WorkflowStepBar({ currentStep, viewingStep, onStepClick }: WorkflowStepBarProps) {
  const theme = useTheme();
  const currentIndex = getStepIndex(currentStep);
  const viewingIndex = getStepIndex(viewingStep);
  const isViewingPastStep = viewingIndex !== currentIndex;

  return (
    <ol
      css={css`
        display: flex;
        align-items: center;
        list-style: none;
        margin: 0;
        padding: ${spacing.xs} 4px;
        margin-bottom: ${spacing.md};
        gap: 0;
        overflow: visible;
      `}
      aria-label="Workflow progress"
    >
      {STEPS.map((step, index) => {
        const Icon = step.icon;
        const isCompleted = index < currentIndex;
        const isCurrent = index === currentIndex;
        const isViewing = index === viewingIndex && isViewingPastStep;
        const state = isCompleted ? 'completed' : isCurrent ? 'current' : 'future';
        const isClickable = isCompleted || (isCurrent && isViewingPastStep);

        const circleColor = isCompleted
          ? theme.colors.success
          : isCurrent
            ? theme.colors.primary
            : theme.colors.textMuted;

        const circleBg = isCompleted
          ? `${theme.colors.success}18`
          : isCurrent
            ? `${theme.colors.primary}30`
            : 'transparent';

        const circleBorder = isCurrent ? `1.5px solid ${theme.colors.primary}` : 'none';

        const circleOutline = isViewing ? `2px solid ${theme.colors.primary}` : 'none';

        const circleContent = (
          <span
            css={css`
              display: flex;
              align-items: center;
              justify-content: center;
              width: 30px;
              height: 30px;
              border-radius: 50%;
              background: ${circleBg};
              border: ${circleBorder};
              outline: ${circleOutline};
              outline-offset: 1px;
              flex-shrink: 0;
            `}
          >
            <Icon size={16} color={circleColor} aria-hidden="true" />
          </span>
        );

        return (
          <li
            key={step.key}
            data-state={state}
            data-viewing={isViewing ? 'true' : undefined}
            css={css`
              display: flex;
              align-items: center;
              flex: ${index < STEPS.length - 1 ? 1 : 0};
            `}
          >
            {isClickable ? (
              <button
                type="button"
                onClick={() => onStepClick(step.key)}
                aria-label={step.label}
                title={step.label}
                css={css`
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  padding: 0;
                  border: none;
                  background: none;
                  cursor: pointer;
                  border-radius: 50%;
                  transition: transform 0.15s ease;
                  flex-shrink: 0;

                  &:hover {
                    transform: scale(1.15);
                  }
                `}
              >
                {circleContent}
              </button>
            ) : (
              <span aria-label={step.label} title={step.label}>
                {circleContent}
              </span>
            )}
            {index < STEPS.length - 1 && (
              <span
                aria-hidden="true"
                css={css`
                  flex: 1;
                  height: ${index < currentIndex ? '2px' : '1px'};
                  background: ${index < currentIndex ? theme.colors.success : theme.colors.border};
                  margin: 0 2px;
                  min-width: 8px;
                `}
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}
